import { Activity, Sparkles, Link2, RotateCcw, Loader2, RefreshCw, LogOut, Sun, Moon, Menu, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { useTheme } from "@/contexts/ThemeContext";
import { NAV_ITEMS } from "@/components/trading/Sidebar";

export const TopBar = ({
  onAnalyze, analyzing, onConnect, binanceConnected, onReset,
  autoRefresh, onToggleAutoRefresh, onLogout, active, onNavigate, isAdmin,
}) => {
  const { isLight, toggle } = useTheme();
  const mobileItems = isAdmin ? [...NAV_ITEMS, { id: "admin", label: "Admin Paneli", icon: ShieldCheck }] : NAV_ITEMS;
  return (
    <header
      data-testid="topbar"
      className="sticky top-0 z-10 min-h-16 py-2 flex flex-wrap items-center justify-between px-4 md:px-6 border-b border-[var(--border)] bg-[var(--surface)]/90 backdrop-blur-xl gap-3"
    >
      <div className="flex items-center gap-2 md:gap-3">
        <Sheet>
          <SheetTrigger asChild>
            <Button data-testid="mobile-menu-btn" variant="ghost"
              className="md:hidden h-10 w-10 p-0 text-[var(--text-dim)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-hover)]">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" data-testid="mobile-nav-drawer"
            className="w-[260px] overflow-y-auto bg-[var(--app-bg)] border-r border-[var(--border)] p-0">
            <SheetTitle className="sr-only">Menü</SheetTitle>
            <SheetDescription className="sr-only">Uygulama gezinme menüsü</SheetDescription>
            <div className="h-16 flex items-center gap-3 px-4 border-b border-[var(--border)]">
              <div className="font-head font-black text-[15px] tracking-tight text-[var(--text-primary)]">CRYPTOSENSE</div>
            </div>
            <nav className="py-4 flex flex-col gap-1 px-3">
              {mobileItems.map((item) => {
                const Icon = item.icon;
                const isActive = active === item.id;
                return (
                  <SheetClose asChild key={item.id}>
                    <button
                      data-testid={`mobilenav-${item.id}`}
                      onClick={() => onNavigate?.(item.id)}
                      className={cn(
                        "flex items-center gap-3 rounded-md px-3 h-12 text-sm transition-colors w-full text-left",
                        isActive
                          ? "bg-[#00FFA3]/10 text-[var(--pos)] border border-[#00FFA3]/30"
                          : "text-[var(--text-dim)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)] border border-transparent"
                      )}
                    >
                      <Icon className="h-[18px] w-[18px] flex-shrink-0" strokeWidth={isActive ? 2.4 : 2} />
                      <span className="truncate">{item.label}</span>
                    </button>
                  </SheetClose>
                );
              })}
            </nav>
          </SheetContent>
        </Sheet>
        <h1 className="font-head font-black text-base md:text-xl tracking-tight">Trading Terminal</h1>
        <div className="hidden lg:flex items-center gap-2 rounded-full border border-[var(--border)] bg-[var(--surface)] px-3 py-1">
          <Activity className="h-3.5 w-3.5 text-[var(--pos)]" />
          <span className="text-[11px] text-[var(--text-dim)]">Piyasa Canlı</span>
          <span className="h-1.5 w-1.5 rounded-full bg-[var(--pos)] animate-pulse" />
        </div>
      </div>

      <div className="flex flex-wrap max-w-full items-center gap-2">
        <div className={cn(
          "hidden md:flex items-center gap-2 rounded-md border px-3 h-10 transition-colors",
          autoRefresh ? "border-[#A374FF]/40 bg-[#A374FF]/10" : "border-[var(--border)] bg-[var(--surface)]"
        )}>
          <RefreshCw className={cn("h-3.5 w-3.5", autoRefresh ? "text-[var(--accent)] animate-spin" : "text-[var(--text-faint)]")}
            style={autoRefresh ? { animationDuration: "3s" } : {}} />
          <span className="text-[11px] text-[var(--text-dim)]">Oto Analiz (5dk)</span>
          <Switch data-testid="auto-refresh-toggle" checked={Boolean(autoRefresh)} onCheckedChange={onToggleAutoRefresh}
            className="data-[state=checked]:bg-[var(--accent)]" />
        </div>

        <Button data-testid="reset-portfolio-btn" onClick={onReset} variant="ghost"
          className="hidden xl:flex h-10 text-[var(--text-dim)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-hover)]">
          <RotateCcw className="h-4 w-4 mr-1.5" /> Sıfırla
        </Button>
        <Button data-testid="connect-binance-btn" onClick={onConnect} variant="outline"
          title="Gate.io API Ayarları" aria-label="Gate.io API Ayarları"
          className="h-10 border-[var(--border)] bg-[var(--surface)] text-[var(--text-primary)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)]">
          <Link2 className="h-4 w-4 mr-1.5" />
          <span className="hidden lg:inline">{binanceConnected ? "Gate.io ✓" : "Gate.io"}</span>
        </Button>
        <Button data-testid="analyze-btn" onClick={onAnalyze} disabled={analyzing}
          title="Analiz çalıştır" aria-label="Analiz çalıştır"
          className="h-10 bg-[var(--accent)] text-black font-bold hover:bg-[#b48fff] hover:text-black disabled:opacity-70">
          {analyzing ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Analiz...</>
                     : <><Sparkles className="h-4 w-4 mr-1.5" /> <span className="hidden lg:inline">Analiz Et</span></>}
        </Button>
        <Button data-testid="theme-toggle-btn" onClick={toggle} variant="ghost"
          className="h-10 w-10 p-0 text-[var(--text-dim)] hover:text-[var(--warn)] hover:bg-[var(--surface-hover)]">
          {isLight ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
        </Button>
        <Button data-testid="logout-btn" onClick={onLogout} variant="ghost"
          className="h-10 w-10 p-0 text-[var(--text-dim)] hover:text-[var(--neg)] hover:bg-[#FF3366]/10">
          <LogOut className="h-4 w-4" />
        </Button>
      </div>
    </header>
  );
};
